import { Badge, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectAutoPipeRoutingConnectionPoints,
  selectAutoPipeRoutingPipeElevationsMmAboveFloorBySystem,
  selectAutoPipeRoutingModalOpen,
  selectAutoPipeRoutingPickingConnectionPointInViewer,
  selectAutoPipeRoutingWizardStep,
  selectSelectedAutoPipeRoutingSystemType,
  setAutoPipeRoutingModalOpenInRedux,
  setAutoPipeRoutingWizardStepInRedux,
} from "../../pipeRoutingSlice";
import { AutoPipeRoutingConnectionPointStep } from "./AutoPipeRoutingConnectionPointStep";
import { AutoPipeRoutingCostSettingsStep } from "./AutoPipeRoutingCostSettingsStep";
import { AutoPipeRoutingPipeElevationsStep } from "./AutoPipeRoutingPipeElevationsStep";
import { AutoPipeRoutingPipeSelectionStep } from "./AutoPipeRoutingPipeSelectionStep";
import { AutoPipeRoutingStartStep } from "./AutoPipeRoutingStartStep";
import { AutoPipeRoutingSystemStep } from "./AutoPipeRoutingSystemStep";
import {
  selectAutoPipeRoutingPipeDimension,
  selectAutoPipeRoutingPipeSeriesName,
} from "../../pipeRoutingSlice";

export const AutoPipeRoutingModal = () => {
  const dispatch = useAppDispatch();
  const opened = useAppSelector(selectAutoPipeRoutingModalOpen);
  const pickingConnectionPointInViewer = useAppSelector(
    selectAutoPipeRoutingPickingConnectionPointInViewer
  );
  const wizardStep = useAppSelector(selectAutoPipeRoutingWizardStep);
  const connectionPoints = useAppSelector(selectAutoPipeRoutingConnectionPoints);
  const selectedSystemType = useAppSelector(selectSelectedAutoPipeRoutingSystemType);
  const selectedPipeSeriesName = useAppSelector(selectAutoPipeRoutingPipeSeriesName);
  const selectedPipeDimension = useAppSelector(selectAutoPipeRoutingPipeDimension);
  const pipeElevationsMmAboveFloorBySystem = useAppSelector(
    selectAutoPipeRoutingPipeElevationsMmAboveFloorBySystem
  );

  const selectedPipeElevations = selectedSystemType
    ? pipeElevationsMmAboveFloorBySystem[selectedSystemType]
    : undefined;

  useEffect(() => {
    if (!opened) {
      dispatch(setAutoPipeRoutingWizardStepInRedux(1));
      return;
    }
  }, [dispatch, opened]);

  const canContinueFromStep1 = selectedSystemType != null;
  const canContinueFromStep2 = selectedPipeSeriesName != null && selectedPipeDimension != null;
  const xDirectionMmAboveFloor = selectedPipeElevations?.xDirectionMmAboveFloor ?? 0;
  const yDirectionMmAboveFloor = selectedPipeElevations?.yDirectionMmAboveFloor ?? 0;
  const hasValidXDirectionMmAboveFloor =
    xDirectionMmAboveFloor >= 100 && xDirectionMmAboveFloor <= 8000;
  const hasValidYDirectionMmAboveFloor =
    yDirectionMmAboveFloor >= 100 && yDirectionMmAboveFloor <= 8000;
  const canContinueFromStep3 =
    selectedSystemType != null && hasValidXDirectionMmAboveFloor && hasValidYDirectionMmAboveFloor;
  const canContinueFromStep4 = connectionPoints.length > 0;

  const stepTitle = useMemo(() => {
    if (wizardStep === 1) {
      return "Velg hvilket system du vil lage automatisk rordragning for";
    }
    if (wizardStep === 2) {
      return "Velg rørserie og dimensjon";
    }
    if (wizardStep === 3) {
      return "Angi rorhoyder for X- og Y-retning";
    }
    if (wizardStep === 4) {
      return "Velg anslutningspunkter";
    }
    if (wizardStep === 5) {
      return "Juster routingparametre";
    }

    return "Start automatisk rordragning";
  }, [wizardStep]);

  const stepDescription = useMemo(() => {
    if (wizardStep === 1) {
      return "Dette valget styrer hvilke enheter som tas med videre i oppsettet.";
    }
    if (wizardStep === 2) {
      return "Velg hvilken rorserie og dimensjon som skal brukes i losningen.";
    }
    if (wizardStep === 3) {
      return "Angi hoydene i millimeter over gulv for horisontale ror i hver retning.";
    }
    if (wizardStep === 4) {
      return "Pek ut punktene i viewer der rorene skal kobles til.";
    }
    if (wizardStep === 5) {
      return "Finjuster penalties og vekter for hvordan rorene trekkes.";
    }

    return "Kontroller oppsettet og start rordragningen.";
  }, [wizardStep]);

  const handleClose = () => {
    dispatch(setAutoPipeRoutingModalOpenInRedux(false));
  };

  return (
    <Modal
      opened={opened && !pickingConnectionPointInViewer}
      onClose={handleClose}
      title="Automatisk rordragning"
      centered
      size="lg"
    >
      <Stack gap="md">
        <div>
          <Badge variant="light" mb="xs">
            Steg {wizardStep} av 6
          </Badge>
          <Text fw={600}>{stepTitle}</Text>
          <Text size="sm" c="dimmed">
            {stepDescription}
          </Text>
        </div>

        {wizardStep === 1 && <AutoPipeRoutingSystemStep />}

        {wizardStep === 2 && <AutoPipeRoutingPipeSelectionStep />}

        {wizardStep === 3 && <AutoPipeRoutingPipeElevationsStep />}

        {wizardStep === 4 && <AutoPipeRoutingConnectionPointStep />}

        {wizardStep === 5 && <AutoPipeRoutingCostSettingsStep />}

        {wizardStep === 6 && <AutoPipeRoutingStartStep />}

        <Group justify="flex-end">
          {wizardStep > 1 && (
            <Button
              variant="subtle"
              onClick={() =>
                dispatch(
                  setAutoPipeRoutingWizardStepInRedux((wizardStep - 1) as 1 | 2 | 3 | 4 | 5)
                )
              }
            >
              Tilbake
            </Button>
          )}
          <Button variant="subtle" onClick={handleClose}>
            Steng
          </Button>
          {wizardStep === 1 && (
            <Button
              onClick={() => dispatch(setAutoPipeRoutingWizardStepInRedux(2))}
              disabled={!canContinueFromStep1}
            >
              Neste
            </Button>
          )}
          {wizardStep === 2 && (
            <Button
              onClick={() => dispatch(setAutoPipeRoutingWizardStepInRedux(3))}
              disabled={!canContinueFromStep2}
            >
              Neste
            </Button>
          )}
          {wizardStep === 3 && (
            <Button
              onClick={() => dispatch(setAutoPipeRoutingWizardStepInRedux(4))}
              disabled={!canContinueFromStep3}
            >
              Neste
            </Button>
          )}
          {wizardStep === 4 && (
            <Button
              onClick={() => dispatch(setAutoPipeRoutingWizardStepInRedux(5))}
              disabled={!canContinueFromStep4}
            >
              Neste
            </Button>
          )}
          {wizardStep === 5 && (
            <Button onClick={() => dispatch(setAutoPipeRoutingWizardStepInRedux(6))}>Neste</Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
};
